import { Directive, ElementRef, NgZone, OnInit, Optional } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { DOWN_ARROW, ENTER, ESCAPE, TAB, UP_ARROW } from '@angular/cdk/keycodes';
import { filter, takeUntil } from 'rxjs/operators';
import { DestroyableComponent } from 'app/main/destroyable.component';

/**
 * Da applicare all'input di ricerca proiettato dentro un <mat-select>.
 *
 * Risolve tre problemi introdotti da Material MDC:
 * - openedChange viene emesso in un microtask subito dopo attachOverlay, prima che
 *   l'overlay sia posizionato: un focus() sincrono non attecchisce. Va differito.
 * - nelle select multiple MatSelect._onSelect() riporta il focus sul trigger a ogni
 *   selezione utente, quindi va ripristinato sull'input.
 * - il keydown va fermato per non finire nella typeahead di MatSelect, ma solo per i
 *   tasti di scrittura: Esc/Tab/frecce/Invio devono continuare a risalire (il dispatcher
 *   tastiera della CDK ascolta in bubbling su body).
 */
@Directive({
	selector: 'input[selectPanelSearch]',
	host: {
		'(keydown)': 'onKeydown($event)'
	},
	standalone: false
})
export class SelectPanelSearchDirective extends DestroyableComponent implements OnInit{

	/** Tasti che devono raggiungere MatSelect e il dispatcher dell'overlay. */
	private static readonly PASS_THROUGH_KEYS = [UP_ARROW, DOWN_ARROW, ENTER, ESCAPE, TAB];

	constructor(
		private elementRef: ElementRef<HTMLInputElement>,
		private zone: NgZone,
		@Optional() private matSelect: MatSelect
	){
		super();
	}

	ngOnInit(): void{
		if(!this.matSelect){
			console.warn('selectPanelSearch: nessun MatSelect nell\'injector, l\'input va applicato dentro un <mat-select>');
			return;
		}

		this.matSelect.openedChange
		.pipe(filter(isOpen => isOpen), takeUntil(this._unsubscribeAll))
		.subscribe(() => this.focusDeferred());

		// Nelle select multiple il pannello resta aperto e MatSelect ruba il focus a ogni scelta.
		this.matSelect.selectionChange
		.pipe(filter(() => this.matSelect.multiple && this.matSelect.panelOpen), takeUntil(this._unsubscribeAll))
		.subscribe(() => this.focusDeferred());
	}

	onKeydown(event: KeyboardEvent): void{
		if(SelectPanelSearchDirective.PASS_THROUGH_KEYS.includes(event.keyCode)) return;
		event.stopPropagation();
	}

	private focusDeferred(): void{
		this.zone.runOutsideAngular(() => {
			setTimeout(() => this.elementRef.nativeElement.focus());
		});
	}
}
